import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getHomeSummary, type SummaryScope } from "@/api/homeSummary";
import type { AllowedRoute, QueueWidget } from "@/api/types";

const ONE_MINUTE = 60_000;

/**
 * NAV-1 left-nav badge hydration (AC-NAV-BADGE-PERF / -SOURCE / -SCOPE).
 *
 * ONE /Custom/frontend/home-summary payload for the whole sidebar — zero
 * per-item endpoints. The payload scope follows the backend-assigned
 * badgeScope (uniform per role: my / team / all), so the count and the
 * click-target (?scope=team) always agree. Refresh: app-load (mount) +
 * focus-return (refetchOnWindowFocus) + after-action (every mutation hook
 * already invalidates the ["homeSummary"] prefix). The 60s staleTime simply
 * reuses the home-summary caching window — no dedicated sidebar polling.
 *
 * Widget unavailable or request failed → badge HIDDEN, never a stale count.
 */
export function useNavBadges(allowedRoutes: AllowedRoute[]) {
  const bindings = useMemo(
    () => allowedRoutes.filter((r) => r.accessMode !== "hidden" && r.badgeBinding),
    [allowedRoutes],
  );

  // badgeScope is uniform across a role's routes (backend contract); take the first.
  const scope: SummaryScope =
    bindings[0]?.badgeScope === "team" ? "team" : bindings[0]?.badgeScope === "all" ? "all" : "my";

  const query = useQuery({
    queryKey: ["homeSummary", "navBadges", scope],
    queryFn: ({ signal }) => getHomeSummary(scope, signal),
    staleTime: ONE_MINUTE,
    refetchOnWindowFocus: true,
    enabled: bindings.length > 0,
  });

  const widgets = query.data?.widgets as Record<string, QueueWidget | undefined> | undefined;

  function badgeCount(binding?: string): number | undefined {
    if (!binding || query.isError || !query.data) return undefined;
    // actionQueueTotal is a top-level F1 number (not a {count,route} widget).
    if (binding === "actionQueueTotal") {
      const n = query.data.actionQueueTotal;
      return typeof n === "number" && n > 0 ? n : undefined;
    }
    // myInternalRequests is a top-level v1.2.2 additive key, not a widgets.* member.
    const widget =
      binding === "myInternalRequests" ? query.data.myInternalRequests : widgets?.[binding];
    const count = widget?.count;
    return typeof count === "number" && count > 0 ? count : undefined;
  }

  return { badgeCount };
}
